<template>
  <div class="bg-background-light text-slate-800 antialiased h-screen flex overflow-hidden">
    <AppSidebar currentPage="documents" />

    <main class="flex-1 flex flex-col h-full overflow-hidden">

      <!-- Header -->
      <header class="h-16 bg-surface border-b border-slate-200 flex items-center justify-between px-4 md:px-8 flex-shrink-0">
        <div>
          <h2 class="text-lg font-bold text-slate-900">Document Vault</h2>
          <p class="text-xs text-slate-500">Securely manage client files, financial records, and correspondence.</p>
        </div>

      </header>

      <div class="flex-1 overflow-y-auto p-8">
        <div class="max-w-[1400px] mx-auto space-y-6">

          <!-- Search & Filter Bar -->
          <div class="bg-surface rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div class="px-4 md:px-6 py-4 border-b border-slate-100 flex flex-col md:flex-row md:items-center gap-3">
              <div class="relative flex-1 md:max-w-xs">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined" style="font-size: 18px;">search</span>
                <input v-model="searchQuery" type="text" placeholder="Search client folders..."
                  class="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
              </div>
              <div class="flex items-center gap-3 flex-wrap">
                <div class="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
                  <button
                    @click="activeSubfolderFilter = ''"
                    class="px-3 py-1 rounded-md text-xs font-medium transition-colors"
                    :class="activeSubfolderFilter === '' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'">
                    All
                  </button>
                  <button
                    v-for="sub in subFolderNames" :key="sub"
                    @click="activeSubfolderFilter = sub"
                    class="px-3 py-1 rounded-md text-xs font-medium transition-colors"
                    :class="activeSubfolderFilter === sub ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'">
                    {{ sub }}
                  </button>
                </div>
                <span class="text-xs text-slate-500 whitespace-nowrap">{{ filteredFolders.length }} client(s)</span>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="flex flex-col items-center justify-center py-20">
              <svg class="animate-spin h-10 w-10 text-primary mb-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p class="text-slate-400 text-sm">Loading client folders from SharePoint...</p>
            </div>

            <!-- Empty -->
            <div v-else-if="filteredFolders.length === 0" class="text-center py-16">
              <span class="material-symbols-outlined text-slate-300" style="font-size: 48px;">folder_off</span>
              <p class="text-slate-400 text-sm mt-2">
                {{ searchQuery ? `No folders match "${searchQuery}"` : 'No client folders found in SharePoint.' }}
              </p>
            </div>

            <!-- Client Folder Accordion List -->
            <div v-else class="divide-y divide-slate-100">
              <div v-for="folder in filteredFolders" :key="folder.Name">

                <!-- Folder Header Row -->
                <div
                  @click="toggleFolder(folder.Name)"
                  class="flex items-center justify-between px-4 md:px-6 py-4 cursor-pointer hover:bg-slate-50/70 transition-colors select-none gap-3">
                  <div class="flex items-center gap-3 min-w-0 flex-1">
                    <span
                      class="material-symbols-outlined text-slate-400 transition-transform duration-200 flex-shrink-0"
                      :class="isExpanded(folder.Name) ? 'rotate-90' : ''"
                      style="font-size: 20px;">
                      chevron_right
                    </span>
                    <div class="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      :class="isExpanded(folder.Name) ? 'bg-primary/10' : 'bg-amber-50'">
                      <span class="material-symbols-outlined"
                        :class="isExpanded(folder.Name) ? 'text-primary' : 'text-amber-500'"
                        style="font-size: 20px;">
                        {{ isExpanded(folder.Name) ? 'folder_open' : 'folder' }}
                      </span>
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-semibold text-slate-900 truncate">{{ folder.Name }}</p>
                      <p class="text-xs text-slate-400 mt-0.5">{{ getFolderMeta(folder.Name) }}</p>
                    </div>
                  </div>

                  <!-- Actions -->
                  <div class="flex items-center gap-2 flex-shrink-0" @click.stop>
                    <button
                      @click="openUploadModal(folder.Name)"
                      class="rounded-lg bg-primary text-secondary font-bold hover:bg-primary-hover transition-all flex items-center gap-1.5 px-2 py-1.5 md:px-3 text-xs">
                      <span class="material-symbols-outlined" style="font-size: 15px;">upload</span>
                      <span class="hidden sm:inline">Upload</span>
                    </button>
                    <a
                      :href="`https://abctaxadvisory.sharepoint.com${folder.ServerRelativeUrl}`"
                      target="_blank"
                      @click.stop
                      class="rounded-lg bg-slate-100 text-slate-600 font-semibold hover:bg-slate-200 transition-all flex items-center gap-1.5 px-2 py-1.5 md:px-3 text-xs">
                      <span class="material-symbols-outlined" style="font-size: 15px;">open_in_new</span>
                      <span class="hidden sm:inline">SharePoint</span>
                    </a>
                  </div>
                </div>

                <!-- Expanded Content -->
                <div v-if="isExpanded(folder.Name)" class="bg-slate-50/40 border-t border-slate-100 px-4 md:px-6 pb-5 pt-4">

                  <!-- Loading subfolders -->
                  <div v-if="isFolderLoading(folder.Name)" class="flex justify-center py-6">
                    <svg class="animate-spin h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>

                  <div v-else>
                    <!-- Subfolder tabs -->
                    <div class="flex items-center gap-1 bg-white border border-slate-200 p-1 rounded-lg mb-4 shadow-sm overflow-x-auto max-w-full">
                      <button
                        v-for="sub in subFolderNames"
                        :key="sub"
                        @click="setActiveSubfolder(folder.Name, sub)"
                        class="px-3 py-1.5 rounded-md text-xs font-semibold transition-colors flex items-center gap-1.5"
                        :class="getActiveSubfolder(folder.Name) === sub
                          ? 'bg-primary text-secondary shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'">
                        <span class="material-symbols-outlined" style="font-size: 14px;">{{ getSubfolderIcon(sub) }}</span>
                        {{ sub }}
                        <span
                          v-if="getSubfolderFiles(folder.Name, sub).length"
                          class="px-1.5 py-0.5 rounded-full text-xs font-bold"
                          :class="getActiveSubfolder(folder.Name) === sub
                            ? 'bg-white/20 text-white'
                            : 'bg-slate-100 text-slate-500'">
                          {{ getSubfolderFiles(folder.Name, sub).length }}
                        </span>
                      </button>
                    </div>

                    <!-- Empty subfolder -->
                    <div
                      v-if="getSubfolderFiles(folder.Name, getActiveSubfolder(folder.Name)).length === 0"
                      class="text-center py-10 bg-white rounded-xl border border-dashed border-slate-200">
                      <span class="material-symbols-outlined text-slate-300" style="font-size: 36px;">folder_open</span>
                      <p class="text-slate-400 text-sm mt-2">No files in "{{ getActiveSubfolder(folder.Name) }}"</p>
                      <button
                        @click="openUploadModal(folder.Name, getActiveSubfolder(folder.Name))"
                        class="mt-2 text-xs text-primary hover:underline">
                        Upload the first file here
                      </button>
                    </div>

                    <!-- Files table -->
                    <div v-else class="rounded-xl border border-slate-200 overflow-hidden bg-white">
                      <table class="w-full text-left">
                        <thead>
                          <tr class="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200">
                            <th class="px-5 py-3">Document Name</th>
                            <th class="px-5 py-3">Category</th>
                            <th class="px-5 py-3">Date Uploaded</th>
                            <th class="px-5 py-3">Size</th>
                            <th class="px-5 py-3 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody class="divide-y divide-slate-100">
                          <tr
                            v-for="doc in getSubfolderFiles(folder.Name, getActiveSubfolder(folder.Name))"
                            :key="doc.FileUrl"
                            class="hover:bg-slate-50/50 transition-colors group">
                            <td class="px-5 py-3">
                              <div class="flex items-center gap-3">
                                <div class="p-1.5 rounded-lg" :class="getDocBgClass(getFileType(doc.Name))">
                                  <span class="material-symbols-outlined" :class="getDocIconClass(getFileType(doc.Name))" style="font-size: 18px;">
                                    {{ getDocIcon(getFileType(doc.Name)) }}
                                  </span>
                                </div>
                                <span class="text-sm font-medium text-slate-900 truncate max-w-[280px]">{{ doc.Name }}</span>
                              </div>
                            </td>
                            <td class="px-5 py-3">
                              <span class="px-2.5 py-1 rounded-full text-xs font-medium" :class="getCategoryClass(getActiveSubfolder(folder.Name))">
                                {{ getActiveSubfolder(folder.Name) }}
                              </span>
                            </td>
                            <td class="px-5 py-3">
                              <span class="text-sm text-slate-500">{{ formatDate(doc.Created) }}</span>
                            </td>
                            <td class="px-5 py-3">
                              <span class="text-sm text-slate-500">{{ formatFileSize(doc.Size) }}</span>
                            </td>
                            <td class="px-5 py-3 text-right">
                              <div class="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <a
                                  :href="buildFileUrl(doc)"
                                  target="_blank"
                                  class="p-1.5 text-slate-400 hover:text-primary transition-colors rounded-lg hover:bg-primary/10 inline-flex"
                                  title="Download">
                                  <span class="material-symbols-outlined" style="font-size: 18px;">download</span>
                                </a>
                                <button
                                  @click="confirmDeleteFile(folder.Name, doc)"
                                  class="p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                                  title="Delete">
                                  <span class="material-symbols-outlined" style="font-size: 18px;">delete</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    </main>

    <!-- ===== UPLOAD MODAL ===== -->
    <div v-if="showUploadModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div class="flex items-center justify-between mb-5">
          <h3 class="text-base font-bold text-slate-900 flex items-center gap-2">
            <span class="material-symbols-outlined text-primary" style="font-size: 20px;">upload_file</span>
            Upload to {{ uploadTarget.clientName }}
          </h3>
          <button @click="showUploadModal = false" class="text-slate-400 hover:text-slate-600">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>

        <div class="space-y-4">
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Destination Folder</label>
            <select v-model="uploadTarget.subfolder"
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white">
              <option v-for="sub in subFolderNames" :key="sub" :value="sub">{{ sub }}</option>
            </select>
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Select Files</label>
            <div
              class="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-primary/40 transition-colors"
              @click="$refs.fileInput.click()">
              <span class="material-symbols-outlined text-slate-300" style="font-size: 36px;">cloud_upload</span>
              <p class="text-sm text-slate-500 mt-2">
                <span v-if="uploadFiles.length === 0">Click to select files</span>
                <span v-else class="font-medium text-primary">{{ uploadFiles.length }} file(s) selected</span>
              </p>
              <p v-if="uploadFiles.length === 0" class="text-xs text-slate-400 mt-1">PDF, DOCX, XLSX, JPG, PNG</p>
              <ul v-else class="mt-2 text-xs text-slate-500 space-y-1">
                <li v-for="(f, i) in uploadFiles" :key="i">{{ f.name }}</li>
              </ul>
            </div>
            <input ref="fileInput" type="file" class="hidden" multiple @change="onFilesSelected" />
          </div>

          <div v-if="uploading && uploadProgress.length" class="space-y-2">
            <div v-for="(p, i) in uploadProgress" :key="i" class="flex items-center gap-3">
              <span class="material-symbols-outlined text-slate-400" style="font-size: 16px;">insert_drive_file</span>
              <div class="flex-1">
                <p class="text-xs font-medium text-slate-700 truncate">{{ p.name }}</p>
                <div class="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div class="h-full rounded-full transition-all"
                    :class="p.done ? 'bg-green-500' : 'bg-primary'"
                    :style="{ width: p.done ? '100%' : '60%' }"></div>
                </div>
              </div>
              <span class="text-xs" :class="p.done ? 'text-green-600' : 'text-slate-400'">
                {{ p.done ? 'Done' : 'Uploading...' }}
              </span>
            </div>
          </div>

          <div v-if="uploadError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
            <span class="material-symbols-outlined" style="font-size: 16px;">error</span>{{ uploadError }}
          </div>
          <div v-if="uploadSuccess" class="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
            <span class="material-symbols-outlined" style="font-size: 16px;">check_circle</span>{{ uploadSuccess }}
          </div>

          <div class="flex gap-3 pt-2">
            <button @click="showUploadModal = false"
              class="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button @click="handleUpload" :disabled="uploading || uploadFiles.length === 0"
              class="flex-1 py-2.5 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              <svg v-if="uploading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span v-else class="material-symbols-outlined" style="font-size: 18px;">upload</span>
              {{ uploading ? 'Uploading...' : 'Upload' }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import AppSidebar from '../components/AppSidebar.vue';

export default {
  name: 'AdminDocumentVault',
  components: { AppSidebar },

  data() {
    return {
      loading: true,
      searchQuery: '',
      activeSubfolderFilter: '',
      subFolderNames: ['Uploaded Documents', 'Correspondence'],
      allFolders: [],
      // ✅ Vue 3: plain objects instead of new Set() — no this.$set anywhere
      expandedFolders: {},
      loadingFolders: {},
      folderData: {},
      activeSubfolders: {},
      showUploadModal: false,
      uploading: false,
      uploadFiles: [],
      uploadProgress: [],
      uploadError: '',
      uploadSuccess: '',
      uploadTarget: { clientName: '', subfolder: 'Uploaded Documents' }
    };
  },

  computed: {
    filteredFolders() {
      let folders = this.allFolders;
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        folders = folders.filter(f => f.Name.toLowerCase().includes(q));
      }
      if (this.activeSubfolderFilter) {
        folders = folders.filter(f => {
          if (!this.folderData[f.Name]) return true;
          return (this.folderData[f.Name][this.activeSubfolderFilter] || []).length > 0;
        });
      }
      return folders;
    },

    totalSize() {
      let bytes = 0;
      Object.values(this.folderData).forEach(structure => {
        Object.values(structure).forEach(files => {
          files.forEach(f => { bytes += f.Size || 0; });
        });
      });
      return this.formatFileSize(bytes);
    }
  },

  async mounted() {
    await this.loadAllFolders();
  },

  methods: {
    async loadAllFolders() {
      this.loading = true;
      try {
        this.allFolders = await this.$sharepoint.getAllClientFolders();
      } catch (error) {
        console.error('Error loading client folders:', error);
        this.allFolders = [];
      } finally {
        this.loading = false;
      }
    },

    async toggleFolder(clientName) {
      if (this.expandedFolders[clientName]) {
        // ✅ Spread triggers Vue 3 reactivity
        this.expandedFolders = { ...this.expandedFolders, [clientName]: false };
        return;
      }
      this.expandedFolders = { ...this.expandedFolders, [clientName]: true };

      if (!this.folderData[clientName]) {
        await this.loadFolderFiles(clientName);
      }
      if (!this.activeSubfolders[clientName]) {
        this.activeSubfolders = {
          ...this.activeSubfolders,
          [clientName]: this.activeSubfolderFilter || 'Uploaded Documents'
        };
      }
    },

    async loadFolderFiles(clientName) {
      // ✅ No this.$set — spread assignment for reactivity
      this.loadingFolders = { ...this.loadingFolders, [clientName]: true };
      try {
        const structure = await this.$sharepoint.getClientFolderStructure(clientName);
        this.folderData = {
          ...this.folderData,
          [clientName]: {
            'Uploaded Documents': structure['Uploaded Documents'] || [],
            'Completed Returns': structure['Completed Returns'] || [],
            'Correspondence': structure['Correspondence'] || []
          }
        };
      } catch (error) {
        console.error(`Error loading files for ${clientName}:`, error);
        this.folderData = {
          ...this.folderData,
          [clientName]: {
            'Uploaded Documents': [],
            'Completed Returns': [],
            'Correspondence': []
          }
        };
      } finally {
        this.loadingFolders = { ...this.loadingFolders, [clientName]: false };
      }
    },

    isExpanded(clientName) {
      return !!this.expandedFolders[clientName];
    },

    isFolderLoading(clientName) {
      return !!this.loadingFolders[clientName];
    },

    // ✅ No this.$set — spread assignment
    setActiveSubfolder(clientName, sub) {
      this.activeSubfolders = { ...this.activeSubfolders, [clientName]: sub };
    },

    getActiveSubfolder(clientName) {
      return this.activeSubfolders[clientName] || 'Uploaded Documents';
    },

    getSubfolderFiles(clientName, sub) {
      return this.folderData[clientName]?.[sub] || [];
    },

    getFolderMeta(clientName) {
      if (!this.folderData[clientName]) return 'Click to expand';
      const total = Object.values(this.folderData[clientName])
        .reduce((sum, files) => sum + files.length, 0);
      return `${total} file${total !== 1 ? 's' : ''} across 3 subfolders`;
    },

    subfolderCount(sub) {
      let count = 0;
      Object.values(this.folderData).forEach(structure => {
        count += (structure[sub] || []).length;
      });
      return count;
    },

    getSubfolderIcon(sub) {
      const map = {
        'Uploaded Documents': 'upload_file',
        'Completed Returns': 'task_alt',
        'Correspondence': 'mail'
      };
      return map[sub] || 'folder';
    },

    openUploadModal(clientName, subfolder = 'Uploaded Documents') {
      this.uploadTarget = { clientName, subfolder };
      this.uploadFiles = [];
      this.uploadProgress = [];
      this.uploadError = '';
      this.uploadSuccess = '';
      this.uploading = false;
      this.showUploadModal = true;
    },

    onFilesSelected(event) {
      this.uploadFiles = Array.from(event.target.files);
      this.uploadError = '';
      this.uploadSuccess = '';
    },

    async handleUpload() {
      if (!this.uploadFiles.length) return;
      this.uploadError = '';
      this.uploadSuccess = '';
      this.uploading = true;
      this.uploadProgress = this.uploadFiles.map(f => ({ name: f.name, done: false }));

      try {
        for (let i = 0; i < this.uploadFiles.length; i++) {
          const file = this.uploadFiles[i];
          const uploaded = await this.$sharepoint.uploadDocument(file, {
            clientName: this.uploadTarget.clientName,
            category: this.uploadTarget.subfolder
          });
          this.uploadProgress[i].done = true;

          // ✅ Spread to append file reactively — no direct mutation
          const existing = this.folderData[this.uploadTarget.clientName];
          if (existing) {
            this.folderData = {
              ...this.folderData,
              [this.uploadTarget.clientName]: {
                ...existing,
                [this.uploadTarget.subfolder]: [
                  ...(existing[this.uploadTarget.subfolder] || []),
                  uploaded
                ]
              }
            };
          }
        }

        this.uploadSuccess = `✅ ${this.uploadFiles.length} file(s) uploaded to "${this.uploadTarget.subfolder}"!`;
        this.activeSubfolders = {
          ...this.activeSubfolders,
          [this.uploadTarget.clientName]: this.uploadTarget.subfolder
        };
        setTimeout(() => { this.showUploadModal = false; }, 1500);
      } catch (error) {
        this.uploadError = 'Upload failed. Please check permissions and try again.';
        console.error('Upload error:', error);
      } finally {
        this.uploading = false;
      }
    },

    async confirmDeleteFile(clientName, file) {
      if (!confirm(`Delete "${file.Name}"?`)) return;
      try {
        await this.$sharepoint.deleteDocument(file.FileUrl);
        // ✅ Spread to remove file reactively
        const existing = this.folderData[clientName];
        if (existing) {
          const updated = {};
          for (const sub of this.subFolderNames) {
            updated[sub] = (existing[sub] || []).filter(f => f.FileUrl !== file.FileUrl);
          }
          this.folderData = { ...this.folderData, [clientName]: updated };
        }
      } catch (error) {
        console.error('Failed to delete file:', error);
        alert('Failed to delete file. Please try again.');
      }
    },

    getFileType(filename) {
      if (!filename) return 'file';
      const ext = filename.split('.').pop()?.toLowerCase();
      if (ext === 'pdf') return 'pdf';
      if (['docx', 'doc'].includes(ext)) return 'word';
      if (['xlsx', 'xls'].includes(ext)) return 'excel';
      if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'image';
      return 'file';
    },

    getDocIcon(type) {
      const map = { pdf: 'picture_as_pdf', word: 'description', excel: 'table_view', image: 'image', file: 'insert_drive_file' };
      return map[type] || 'insert_drive_file';
    },

    getDocBgClass(type) {
      const map = { pdf: 'bg-red-50', word: 'bg-blue-50', excel: 'bg-green-50', image: 'bg-purple-50', file: 'bg-slate-50' };
      return map[type] || 'bg-slate-50';
    },

    getDocIconClass(type) {
      const map = { pdf: 'text-red-500', word: 'text-blue-500', excel: 'text-green-500', image: 'text-purple-500', file: 'text-slate-500' };
      return map[type] || 'text-slate-500';
    },

    getCategoryClass(sub) {
      const map = {
        'Uploaded Documents': 'bg-amber-50 text-amber-700',
        'Completed Returns': 'bg-green-50 text-green-700',
        'Correspondence': 'bg-blue-50 text-blue-700'
      };
      return map[sub] || 'bg-slate-50 text-slate-600';
    },

    buildFileUrl(doc) {
      // Route through Netlify proxy — function has its own Graph credentials
      if (doc.DriveItemId && doc.DriveId) {
        return `/.netlify/functions/get-file?driveId=${encodeURIComponent(doc.DriveId)}&itemId=${encodeURIComponent(doc.DriveItemId)}&admin=1`;
      }
      if (doc.FileUrl) {
        return `/.netlify/functions/get-file?path=${encodeURIComponent(doc.FileUrl)}&admin=1`;
      }
      return '#';
    },

    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 B';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      const now = new Date();
      const diffDays = Math.floor((now - date) / 86400000);
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      return date.toLocaleDateString();
    }
  }
};
</script>